import { useRateManagement } from '@api/useFreight/useFreight'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Divider, Form, Select } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import type { CreateAccessorialFormProps, CreateAccessorialFormFooterProps } from './components/types'
import {
  AccessorialInfoCard,
  LocationFiltersCard,
  ServiceRuleCard,
  AdditionalSettingsCard,
  DEFAULT_TIME_INTERVAL,
} from './components'
import { useAccessorialForm } from './hooks/useAccessorialForm'
import { useLocationFilters } from './hooks/useLocationFilters'
import { useFreightAssessorials } from '@/api/useFreightAssessorials'
import type { TimeIntervalFeeConfig } from '@/api/useFreight/types'

export function CreateAccessorialForm({
  ratebook,
  onSuccess,
  editingAccessorial,
  createMutation,
}: CreateAccessorialFormProps) {
  const { form, handleSubmit, initialValues } = useAccessorialForm({
    ratebook,
    onSuccess,
    editingAccessorial,
    createMutation,
  })

  const {
    originEnabled,
    setOriginEnabled,
    destinationEnabled,
    setDestinationEnabled,
    originGroupBy,
    setOriginGroupBy,
    destinationGroupBy,
    setDestinationGroupBy,
    detectGroupingLevel,
  } = useLocationFilters()

  const { useFilteredMetadataQuery, useHolidayCalendarListQuery } = useRateManagement()
  const { data: metadataOptions, isLoading: isMetadataLoading } = useFilteredMetadataQuery(ratebook)
  const { data: holidays = [], isLoading: isHolidaysLoading } = useHolidayCalendarListQuery()
  const { specialPayOptions } = useFreightAssessorials()

  const serviceRuleEnabled = Form.useWatch(['service_rule_form', 'enabled'], form) || false
  const serviceRuleType = Form.useWatch(['service_rule_form', 'type'], form)

  const allLocations = React.useMemo(() => {
    return [...(metadataOptions?.origins || []), ...(metadataOptions?.destinations || [])]
  }, [metadataOptions?.origins, metadataOptions?.destinations])

  React.useEffect(() => {
    if (editingAccessorial && metadataOptions) {
      const hasOriginIds = editingAccessorial.origin_ids?.length > 0
      const hasDestinationIds = editingAccessorial.destination_ids?.length > 0

      setOriginEnabled(hasOriginIds)
      setDestinationEnabled(hasDestinationIds)

      const detectedOriginGroupBy = detectGroupingLevel(editingAccessorial.origins)
      const detectedDestinationGroupBy = detectGroupingLevel(editingAccessorial.destinations)

      setOriginGroupBy(detectedOriginGroupBy)
      setDestinationGroupBy(detectedDestinationGroupBy)

      const originTerminals =
        editingAccessorial.origin_ids?.filter((id) =>
          allLocations.find((loc) => loc.id === id && loc.type === 'Terminal')
        ) || []

      const originSites =
        editingAccessorial.origin_ids?.filter((id) =>
          allLocations.find((loc) => loc.id === id && loc.type === 'Store')
        ) || []

      const destinationTerminals =
        editingAccessorial.destination_ids?.filter((id) =>
          allLocations.find((loc) => loc.id === id && loc.type === 'Terminal')
        ) || []

      const destinationSites =
        editingAccessorial.destination_ids?.filter((id) =>
          allLocations.find((loc) => loc.id === id && loc.type === 'Store')
        ) || []

      // Convert product_group or product_ids to cascader format
      let selectedProducts: string[][] = []
      const productHierarchy = metadataOptions.product_hierarchy

      if (editingAccessorial.product_group) {
        selectedProducts = [[editingAccessorial.product_group]]
      } else if (editingAccessorial.product_ids?.length > 0 && productHierarchy) {
        const productIds = editingAccessorial.product_ids
        const category = Object.keys(productHierarchy).find((cat) =>
          productHierarchy[cat].some((p) => productIds.includes(p.id))
        )

        if (category) {
          const categoryProducts = productHierarchy[category]
          const allSelected = categoryProducts.every((p) => productIds.includes(p.id))

          selectedProducts = allSelected
            ? [[category]]
            : productIds.filter((id) => categoryProducts.some((p) => p.id === id)).map((id) => [category, id])
        }
      }

      const isLinkedToSpecialPay = editingAccessorial.linked_special_pay_config.length > 0

      form.setFieldsValue({
        name: editingAccessorial.name,
        effective_from: dayjs(editingAccessorial.rates[0].effective_from),
        has_effective_to: !!editingAccessorial.rates[0].effective_to,
        effective_to: editingAccessorial.rates[0].effective_to
          ? dayjs(editingAccessorial.rates[0].effective_to)
          : undefined,
        fee_type: editingAccessorial.fee_type.type,
        rate: editingAccessorial.fee_type.type.includes('Percent')
          ? editingAccessorial.rates[0].rate * 100
          : editingAccessorial.rates[0].rate,
        threshold: editingAccessorial.fee_type.threshold || undefined,
        interval: (editingAccessorial.fee_type as TimeIntervalFeeConfig)?.interval || DEFAULT_TIME_INTERVAL,
        max_intervals: (editingAccessorial.fee_type as TimeIntervalFeeConfig)?.max_intervals || undefined,
        selected_products: selectedProducts,
        origin_terminals: originTerminals,
        origin_sites: originSites,
        destination_terminals: destinationTerminals,
        destination_sites: destinationSites,
        requires_approval: editingAccessorial.requires_approval,
        exclude_from_invoice: editingAccessorial.exclude_from_invoice,
        service_rule_form: editingAccessorial.service_rule
          ? {
              enabled: true,
              type: editingAccessorial.service_rule.type,
              threshold_type: editingAccessorial.service_rule.threshold_type || ('time' as const),
            }
          : { enabled: false, threshold_type: 'time' as const },
        link_to_special_pay: isLinkedToSpecialPay,
        linked_special_pay_config: isLinkedToSpecialPay
          ? {
              value: editingAccessorial.linked_special_pay_config[0]?.sp_id || '',
              label: editingAccessorial.linked_special_pay_config[0]?.name || '',
            }
          : undefined,
      })
    } else {
      setOriginGroupBy('Region')
      setDestinationGroupBy('Region')
    }
  }, [editingAccessorial?._id, metadataOptions])

  return (
    <Form
      id='create-accessorial-form'
      form={form}
      layout='vertical'
      onFinish={(values) => handleSubmit(values, metadataOptions)}
      initialValues={initialValues}
      onValuesChange={(changedValues, allValues) => {
        // Only set default type when enabling the toggle for the first time
        if ('service_rule_form' in changedValues && changedValues.service_rule_form?.enabled === true) {
          const currentServiceRule = allValues.service_rule_form || {}
          if (!currentServiceRule.type) {
            // Set default type to Weekend when enabling without a type
            form.setFieldsValue({
              service_rule_form: {
                ...currentServiceRule,
                enabled: true,
                type: 'Weekend',
                threshold_type: currentServiceRule.threshold_type || 'time',
              },
            })
          }
        }
      }}
    >
      <Vertical gap={12} flex='0' style={{ overflow: 'visible' }}>
        <AccessorialInfoCard
          editingAccessorial={editingAccessorial}
          metadataOptions={metadataOptions}
          isMetadataLoading={isMetadataLoading}
          form={form}
        />

        <LocationFiltersCard
          allLocations={allLocations}
          isMetadataLoading={isMetadataLoading}
          originEnabled={originEnabled}
          setOriginEnabled={setOriginEnabled}
          destinationEnabled={destinationEnabled}
          setDestinationEnabled={setDestinationEnabled}
          originGroupBy={originGroupBy}
          setOriginGroupBy={setOriginGroupBy}
          destinationGroupBy={destinationGroupBy}
          setDestinationGroupBy={setDestinationGroupBy}
        />

        <Vertical gap={12} style={{ paddingTop: '16px', paddingBottom: '16px' }}>
          <ServiceRuleCard
            serviceRuleEnabled={serviceRuleEnabled}
            serviceRuleType={serviceRuleType}
            metadataOptions={metadataOptions}
            holidays={holidays}
            isHolidaysLoading={isHolidaysLoading}
          />

          <Divider style={{ margin: '4px 0' }} />

          <Texto category='h4' style={{ marginBottom: 4 }}>
            Additional Settings
          </Texto>
          <AdditionalSettingsCard form={form} specialPayOptions={specialPayOptions} />
        </Vertical>
      </Vertical>
    </Form>
  )
}

export function CreateAccessorialFormFooter({ onCancel, loading, isEditing }: CreateAccessorialFormFooterProps) {
  return (
    <Horizontal
      justifyContent='flex-end'
      gap={8}
      style={{
        padding: '12px 24px',
      }}
    >
      <GraviButton appearance='text' onClick={onCancel}>
        Cancel
      </GraviButton>
      <GraviButton theme2 htmlType='submit' loading={loading} form='create-accessorial-form'>
        {isEditing ? 'Update Accessorial' : 'Create Accessorial'}
      </GraviButton>
    </Horizontal>
  )
}
